import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

interface CalendarDay {
  iso: string;
  day: number;
  inMonth: boolean;
  disabled: boolean;
  isToday: boolean;
  isPast: boolean;
}

export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function shiftIsoDate(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  return toIsoDate(new Date(year, month - 1, day + days));
}

/**
 * Inline month calendar for the audience "Extraction date" filter: picks a single day within
 * ±rangeDays of today, the window served by /webapp/audience/executions/by-date. Purely
 * presentational — the parent owns the selected date and the data behind it.
 */
@Component({
  selector: 'app-extraction-date-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './extraction-date-calendar.component.html',
  styleUrls: ['./extraction-date-calendar.component.scss']
})
export class ExtractionDateCalendarComponent implements OnChanges {
  @Input() selected: string | null = null;
  @Input() rangeDays = 31;
  @Input() locale = 'it';
  @Input() prevLabel = '';
  @Input() nextLabel = '';
  @Output() selectedChange = new EventEmitter<string>();

  readonly today = toIsoDate(new Date());
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth();
  weeks: CalendarDay[][] = [];
  weekdayLabels: string[] = [];
  monthLabel = '';

  constructor() {
    this.build();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected'] && this.selected) {
      const [year, month] = this.selected.split('-').map(Number);
      this.viewYear = year;
      this.viewMonth = month - 1;
    }
    this.build();
  }

  get minDate(): string {
    return shiftIsoDate(this.today, -this.rangeDays);
  }

  get maxDate(): string {
    return shiftIsoDate(this.today, this.rangeDays);
  }

  get canGoPrev(): boolean {
    return toIsoDate(new Date(this.viewYear, this.viewMonth, 1)) > this.minDate;
  }

  get canGoNext(): boolean {
    return toIsoDate(new Date(this.viewYear, this.viewMonth + 1, 0)) < this.maxDate;
  }

  prevMonth(): void {
    if (this.canGoPrev) {
      this.moveView(-1);
    }
  }

  nextMonth(): void {
    if (this.canGoNext) {
      this.moveView(1);
    }
  }

  trackByIso(_: number, day: CalendarDay): string {
    return day.iso;
  }

  select(day: CalendarDay): void {
    if (!day.disabled) {
      this.selectedChange.emit(day.iso);
    }
  }

  private moveView(delta: number): void {
    const next = new Date(this.viewYear, this.viewMonth + delta, 1);
    this.viewYear = next.getFullYear();
    this.viewMonth = next.getMonth();
    this.build();
  }

  private build(): void {
    const locale = this.locale || 'it';
    const monthLabel = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
      .format(new Date(this.viewYear, this.viewMonth, 1));
    this.monthLabel = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

    // Monday-first; 2024-01-01 was a Monday.
    const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
    this.weekdayLabels = Array.from({ length: 7 }, (_, i) => weekdayFormat.format(new Date(2024, 0, 1 + i)));

    const first = new Date(this.viewYear, this.viewMonth, 1);
    const offset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const weekCount = Math.ceil((offset + daysInMonth) / 7);
    const min = this.minDate;
    const max = this.maxDate;

    this.weeks = Array.from({ length: weekCount }, (_, w) =>
      Array.from({ length: 7 }, (_, d) => {
        const date = new Date(this.viewYear, this.viewMonth, 1 - offset + w * 7 + d);
        const iso = toIsoDate(date);
        return {
          iso,
          day: date.getDate(),
          inMonth: date.getMonth() === this.viewMonth,
          disabled: iso < min || iso > max,
          isToday: iso === this.today,
          isPast: iso < this.today
        };
      })
    );
  }
}
